import { EntrySortingType, useTranslation } from "@next-platform/client-common-core";
import { IconVariants, Input, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { memo, type ReactNode, useCallback, useMemo } from "react";

import Dropdown from "@/components/common/Dropdown";
import Trans from "@/components/Trans/Trans";

import { MAX_LOREBOOK_ENTRIES } from "../../constants";

type EntriesListHeaderProps = {
  searchValue: string;
  onSearchChange: (next: string) => void;
  totalEntryCount: number;
  visibleEntryCount: number;
  listHeader?: ReactNode;
  panelTitle?: ReactNode;
  // Sort dropdown. Omitted by read-only consumers (the lorebook detail page), which have no sort
  // affordance, so the control simply does not render there.
  isSortVisible?: boolean;
  sortType?: EntrySortingType;
  onSortChange?: (next: EntrySortingType) => void;
  // Requirement 3: with every entry approved there is nothing to prioritise, so the Priority option
  // is dropped from the menu (the dropdown itself stays, for Latest/Oldest/Name).
  isPrioritySortAvailable?: boolean;
};

function EntriesListHeader({
  searchValue,
  onSearchChange,
  totalEntryCount,
  visibleEntryCount,
  listHeader,
  panelTitle,
  isSortVisible = false,
  sortType = EntrySortingType.PRIORITY,
  onSortChange,
  isPrioritySortAvailable = false,
}: EntriesListHeaderProps) {
  const { t } = useTranslation();

  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value);
    },
    [onSearchChange],
  );

  const sortLabels = useMemo<Record<EntrySortingType, string>>(
    () => ({
      [EntrySortingType.PRIORITY]: t("lorebook:entries.sort.priority"),
      [EntrySortingType.LATEST]: t("lorebook:entries.sort.latest"),
      [EntrySortingType.OLDEST]: t("lorebook:entries.sort.oldest"),
      [EntrySortingType.NAME]: t("lorebook:entries.sort.name"),
    }),
    [t],
  );

  const sortItems = useMemo(() => {
    const options = isPrioritySortAvailable
      ? [EntrySortingType.PRIORITY, EntrySortingType.LATEST, EntrySortingType.OLDEST, EntrySortingType.NAME]
      : [EntrySortingType.LATEST, EntrySortingType.OLDEST, EntrySortingType.NAME];
    return options.map((option) => ({
      label: sortLabels[option],
      value: option,
      onPress: () => onSortChange?.(option),
    }));
  }, [isPrioritySortAvailable, sortLabels, onSortChange]);

  const trimmedSearch = searchValue.trim();
  const isSearchActive = Boolean(trimmedSearch);
  const formattedMaxEntries = MAX_LOREBOOK_ENTRIES.toLocaleString();

  const entryCountContent = useMemo(() => {
    if (isSearchActive) {
      return (
        <Trans
          i18nKey="lorebook:entries.countWithSearch"
          values={{
            visible: visibleEntryCount,
            count: totalEntryCount,
            max: formattedMaxEntries,
          }}
        />
      );
    }

    return <Trans i18nKey="lorebook:entries.count" values={{ count: totalEntryCount, total: formattedMaxEntries }} />;
  }, [formattedMaxEntries, isSearchActive, totalEntryCount, visibleEntryCount]);

  return (
    <div data-testid="EntriesListServer-Header">
      {listHeader}
      <div className="flex flex-col gap-4">
        {panelTitle}
        <Input
          iconStart={IconVariants.SEARCH}
          placeholder={t("lorebook:entries.searchPlaceholder")}
          value={searchValue}
          onChange={handleSearchChange}
          data-testid="EntriesListServer-SearchInput"
        />
        <div className="flex flex-row items-center justify-between gap-4">
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM}>{entryCountContent}</Text>
          {isSortVisible ? (
            <div data-testid="EntriesList-SortDropdown">
              <Dropdown currentValue={sortLabels[sortType]} listItems={sortItems} iconEnd={IconVariants.CHEVRON_DOWN} hasAccessToV2={true} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default memo(EntriesListHeader);
